import * as React from 'react';

import type { FileType } from '@acceligentllc/shared/enums/fileType';

import { Button, TextInput } from '@acceligentllc/storybook';

import CustomModal from 'af-components/CustomModal';
import SubmitButton from 'af-components/SubmitButton';

import styles from './styles.module.scss';

export interface EditAttachmentProps {
	directoryId: Nullable<number>;
	attachmentId: Nullable<number>;
	attachmentName: string;
	attachmentType: Nullable<FileType>;
}

interface Props {
	attachmentData: EditAttachmentProps;
	showModal: boolean;
	setModalVisible: (arg0: boolean) => void;
	updateAttachment: (directoryId: number, attachmentId: number, value: string | undefined) => void;
}

const EditAttachmentModal = React.memo((props: Props) => {
	const { attachmentData: { directoryId, attachmentId, attachmentName, attachmentType }, showModal, setModalVisible, updateAttachment } = props;

	const [name, setName] = React.useState('');

	const baseClassName = 'modal';
	const headerClassName = `${baseClassName}__big-header__thin`;
	const bodyClassName = `${baseClassName}__body`;

	const handleNameChange = React.useCallback((value: string) => {
		setName(value);
	}, []);

	React.useEffect(() => {
		if (showModal) {
			setName(attachmentName);
		}
	}, [showModal, attachmentName]);

	const closeModal = React.useCallback(() => {
		setModalVisible(false);
	}, [setModalVisible]);

	const updateName = React.useCallback(() => {
		if (attachmentId && directoryId && name) {
			const fileName = `${name}${attachmentType}`;
			updateAttachment(directoryId, attachmentId, fileName);
			setModalVisible(false);
		}
	}, [attachmentId, directoryId, name, attachmentType, updateAttachment, setModalVisible]);

	return (
		<CustomModal
			className={styles[baseClassName]}
			closeModal={closeModal}
			modalStyle="info"
			showModal={showModal}
			size="md"
		>
			<CustomModal.Header className={styles[headerClassName]}>
				<span>Rename</span>
			</CustomModal.Header>
			<CustomModal.Body className={styles[bodyClassName]}>
				<TextInput
					error={!name ? 'Name cannot be empty.' : undefined}
					isRequired={true}
					label="File name"
					onChange={handleNameChange}
					placeholder="New name"
					type="text"
					value={name}
				/>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={closeModal}
					style="secondary"
				/>
				<SubmitButton
					disabled={!name}
					label="Save"
					onClick={updateName}
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
});

export default EditAttachmentModal;
